import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  AudioLines,
  CalendarPlus,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileSignature,
  FileText,
  FolderOpen,
  History,
  Hourglass,
  Loader2,
  Lock,
  Mic,
  MicOff,
  Minus,
  Pencil,
  PhoneOff,
  Quote,
  RotateCcw,
  Settings2,
  Skull,
  StickyNote,
  Undo2,
  Upload,
  UserCheck,
  X,
  Zap,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  ONBOARDING_DEAD_REASONS,
  fetchOnboardingTextDraft,
  useClientOnboardingClient,
  useMarkDead,
  useExpedite,
  useReassignClient,
  useSaveOnboardingNotes,
  useWorkChecklist,
  useAdminReengage,
  useSendBackToVaGov,
  useStartFirstCall,
  useUndoFirstCall,
  useSendOnboardingText,
  useSetOnboardingAddress,
  useSetOnboardingAnswer,
  useSpecialists,
  useAcceptSa,
  useStartSaCheck,
  useClientAgreement,
  onboardingAgreementApiFor,
} from '../../../services/clientOnboarding';
import { AgreementSection, FactsSection } from '../intake/signup/signupSections';
import NudgeComposer from '../onboardingSupport/potentialClients/NudgeComposer';
import { useCallCenter } from '../onboardingSupport/callCenter/CallCenterProvider';
import SlotPickerOverlay from '../scheduling/SlotPickerOverlay';
import MeetingActions from '../scheduling/MeetingActions';
import { useUser } from '../../../contexts/UserContext';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { isOwnedBy, NotYoursNote, VIEW_ONLY_TITLE } from '../onboardingSupport/ownership';
import ReengagePicker, { reengageReady } from '../workspace/ReengagePicker';
import { formatPhone, isCarriedSa } from './clientOnboardingUtils';
import LeadTranscriptsCard from '../onboardingSupport/LeadTranscriptsCard';
import { useLeadTranscripts } from '../../../services/leads';
import HotSeatMark from './HotSeatMark';
import SaCheckPanel from './SaCheckPanel';
import VaGovStep from './VaGovStep';
import PoaStep from './PoaStep';
import CmPreferencesStep from './CmPreferencesStep';
import { openSignedPdf } from './SignedSasView';
import { STEP_ICON, StepDetail, pillStatusOf } from './ReadinessChecklist';
import { DEAD_OTHER_MAX, eventBy, eventLine, Picker, tidyHistory } from './ClientOnboardingSlideOver';
import PickerMenu from '../onboardingSupport/PickerMenu';
import {
  AboutCard,
  CallButton,
  ManageCard,
  ManageLabel,
  FolderTabs,
  LABEL,
  NotesEditor,
  PersonGroup,
  PersonRow,
  RAIL_PAD,
  SectionHead,
  TabPage,
  TextButton,
  WorkspaceFrame,
} from '../workspace/WorkspaceKit';
import { toldUsRows } from '../../../lib/leadFacts';
import IdentityRows from './IdentityRows';

// The Client Workspace (Nic 2026-10-09): the Sales Workspace's room for a
// signed client, replacing the busy slide-over for everyone who works the
// page (view only keeps the slide-over). One job per zone:
//   the header: who they are, where they stand, and Text / Call / Book;
//   About them on the left, its own section (who they are, what they told
//     us, their History);
//   the work, one tabbed panel (Nic 2026-10-12): Checklist (the steps on the
//     left, the open step on the right), Files (every file, no folders),
//     Manage (hand off, Move to Dead), then on the right Transcripts and
//     Notes (Onboarding's own, then Sales' notes, read only);
//   the footer: ONE status line and the next thing to do.

// Where they are, left to right (Nic 2026-10-11): VA.gov, their first call,
// the records (with the count), Ready for CM, their Claims Manager. Done is
// green, where they are now is blue with its name in bold, the rest grey.
// Nothing here gates anything: work can happen in any order.
const PipelineTrack = ({ o, va, done, total }) => {
  const withCm = o.state === 'with_cm';
  const ready = o.state === 'ready_for_cm' || withCm;
  const segs = [
    { key: 'va', label: 'VA.gov', done: va.stage !== 'waiting' || ready },
    { key: 'call', label: 'First meeting', done: !!o.firstCall || o.column === 'records' || o.column === 'ptrs' || ready },
    { key: 'records', label: o.carriedOver ? 'Records (carried over)' : `Records ${done}/${total}`, done: ready },
    { key: 'ready', label: 'Ready for CM', done: withCm },
    { key: 'cm', label: 'Claims Manager', done: withCm },
  ];
  const at = segs.findIndex((x) => !x.done);
  return (
    <ol aria-label="Where they are" className="mb-0 mt-[8px] flex w-full max-w-[560px] list-none gap-[4px] pl-0">
      {segs.map((x, i) => {
        const now = i === at;
        return (
          <li key={x.key} aria-current={now ? 'step' : undefined} className="min-w-0 flex-1">
            <div
              className={cn(
                'h-[5px] rounded-full',
                x.done ? 'bg-emerald-500' : now ? 'bg-sky-500' : 'bg-slate-200 dark:!bg-white/10',
              )}
            />
            <div
              className={cn(
                'mt-[4px] truncate text-[11px]',
                now ? 'font-bold text-slate-900 dark:text-white' : x.done ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-slate-400',
              )}
            >
              {x.done && !now ? '✓ ' : ''}
              {x.label}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
const shortDate = (at) => (at ? moment(at).format('MMM D') : '');

// ---- About them and Notes ----------------------------------------------------

const HistoryGroup = ({ events: raw, fieldLabels }) => {
  const [open, setOpen] = useState(false);
  const events = tidyHistory(raw);
  return (
    <div className={RAIL_PAD}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="block w-full rounded-md text-left outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
      >
        <SectionHead
          icon={History}
          tone="violet"
          right={
            <span className="flex items-center gap-[6px]">
              <span className="text-[11px] tabular-nums text-slate-400">{events.length}</span>
              <ChevronDown size={15} className={cn('text-slate-400 transition-transform', open && 'rotate-180')} />
            </span>
          }
        >
          History
        </SectionHead>
      </button>
      {open ? (
        <ol className="mb-0 mt-[10px] list-none space-y-[6px] pl-0">
          {events.map((e) => (
            <li key={e.id} className="flex gap-[10px] text-xs">
              <span className="w-[52px] shrink-0 tabular-nums text-slate-400">{shortDate(e.at)}</span>
              <span className="min-w-0 text-slate-600 dark:text-slate-300" title={eventBy(e)}>
                {eventLine(e, fieldLabels)}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};

// About them (Nic 2026-10-12): its own section on the left, no tabs. Who
// they are, what they told us, then their History. Transcripts and Notes are
// tabs on the right of the work panel.
const AboutPanel = ({ o, lead, events, fieldLabels, identity }) => {
  const snap = lead?.snapshot || null;
  const addr = o.address;
  // Stalled: contact details stay hidden until they're brought back.
  const none = o.contactHidden ? 'Hidden while stalled' : 'None on file';
  return (
    <AboutCard>
          <PersonGroup>
            <PersonRow label="Phone" muted={!o.phone}>
              {o.phone ? formatPhone(o.phone) : none}
            </PersonRow>
            <PersonRow label="Email" title={o.email || undefined} muted={!o.email}>
              {o.email || none}
            </PersonRow>
            <PersonRow
              label="Address"
              muted={!addr}
              title={addr ? `${addr.line1}, ${addr.city}, ${addr.state} ${addr.zip}` : undefined}
            >
              {addr ? `${addr.city}, ${addr.state}${addr.verified ? '' : ' (not verified)'}` : none}
            </PersonRow>
            {o.rating != null ? <PersonRow label="Rating">{o.rating}%</PersonRow> : null}
            {snap?.branch ? <PersonRow label="Branch">{snap.branch}</PersonRow> : null}
            {o.signedAt ? <PersonRow label="Signed">{moment(o.signedAt).format('MMM D, YYYY')}</PersonRow> : null}
            {/* Birthday and SSN, dotted out (Nic 2026-10-25). */}
            <IdentityRows onboardingId={o.onboardingId} identity={identity} />
          </PersonGroup>

          {/* What they told us as clean rows, like the Sales slide-over
              (2026-10-14), then any note someone actually typed. */}
          {lead && toldUsRows(lead).length ? (
            <PersonGroup title="What they told us" icon={Quote} tone="blue">
              {toldUsRows(lead).map(([k, v]) => (
                <PersonRow key={k} label={k}>
                  {v}
                </PersonRow>
              ))}
            </PersonGroup>
          ) : null}
          {lead?.toldUs ? (
            <div className={RAIL_PAD}>
              {toldUsRows(lead).length ? null : (
                <div className="mb-[8px]">
                  <SectionHead icon={Quote} tone="blue">
                    What they told us
                  </SectionHead>
                </div>
              )}
              <p className="mb-0 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
                {lead.toldUs}
              </p>
            </div>
          ) : null}

          <HistoryGroup events={events} fieldLabels={fieldLabels} />
    </AboutCard>
  );
};

// Notes (Nic 2026-10-12): Onboarding's own, editable by their specialist,
// then Sales' notes from their lead, locked.
const NotesTab = ({ o, lead, onboardingId, canEdit }) => {
  const saveNotes = useSaveOnboardingNotes();
  const salesNotes = (lead?.workNotes || '').trim();
  const salesBy = [lead?.workNotesUpdatedByName, lead?.workNotesUpdatedAt ? shortDate(lead.workNotesUpdatedAt) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="divide-y divide-slate-100 dark:divide-white/10">
      <NotesEditor
        key={onboardingId}
        title="Onboarding notes"
        sub={
          o.notesUpdatedAt
            ? `Last saved ${shortDate(o.notesUpdatedAt)}${o.notesUpdatedByName ? ` by ${o.notesUpdatedByName}` : ''}`
            : canEdit
              ? 'Yours to write. Sales sees their own notes, not these.'
              : null
        }
        initial={o.notes || ''}
        readOnly={!canEdit}
        saving={saveNotes.isLoading}
        placeholder="What you learned: conditions, records still coming, who helps them, when to reach them..."
        onSave={async (text) => {
          try {
            await saveNotes.mutateAsync({ onboardingId, notes: text });
          } catch (e) {
            toast.error(e?.response?.data?.msg || "Notes couldn't be saved.");
            throw e;
          }
        }}
      />
      <div className={RAIL_PAD}>
        <div className="mb-[8px]">
          <SectionHead
            icon={Lock}
            tone="slate"
            right={<span className="text-[11px] text-slate-400">Read only</span>}
          >
            Sales&apos; notes
          </SectionHead>
          {salesBy ? <div className="mt-[2px] pl-[32px] text-[11.5px] text-slate-400">{salesBy}</div> : null}
        </div>
        {salesNotes ? (
          <p className="mb-0 whitespace-pre-wrap rounded-lg bg-slate-50 px-[12px] py-[10px] text-[13px] leading-relaxed text-slate-600 ring-1 ring-slate-100 dark:!bg-white/[0.03] dark:text-slate-300 dark:ring-white/5">
            {salesNotes}
          </p>
        ) : (
          <p className="mb-0 text-[13px] italic text-slate-400">Sales didn&apos;t leave notes.</p>
        )}
      </div>
    </div>
  );
};

// The strips above the checklist (Nic 2026-10-11: "no more gating, open
// the same workspace no matter what"). The checklist is always there; the
// call is something the specialist does to help with it. Each strip says
// where that piece stands and carries its one action.
const STRIP = 'flex flex-wrap items-center gap-x-[10px] gap-y-[6px] border-b border-slate-100 px-[14px] py-[8px] text-[13px] dark:border-white/10';
const STRIP_BTN = 'inline-flex h-[28px] items-center gap-[6px] whitespace-nowrap rounded-lg px-[10px] text-xs font-semibold outline-none focus:outline-none disabled:opacity-50';
const STRIP_PRIMARY = cn(STRIP_BTN, 'bg-emerald-600 text-white hover:bg-emerald-500');
const STRIP_SECONDARY = cn(STRIP_BTN, 'text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/5 dark:hover:text-white');

// Their first meeting, until it happens (Nic 2026-10-12, like Sales): a
// booked meeting counts on its own once it's over unless it's marked missed,
// so there is nothing to confirm. For anything not booked, Mark it happened
// with a short reason. The checklist works either way. The meeting itself
// (in-app since 2026-10-25) carries Reschedule, Cancel, Missed and Join.
const isAhead = (m) => !!m && new Date(m.startTime).getTime() > Date.now();
const isToday = (m) => !!m && moment(m.startTime).isSame(moment(), 'day');

const REASON_MAX = 120;
// How a first call counted, for the header line (lib firstCallOf's `via`).
const FIRST_CALL_VIA = { hot_seat: 'by hot transfer ', meeting_kept: 'at their meeting ', manual: 'marked by hand ' };
const CallStrip = ({ o, onboardingId, canAct, busy, onBook, onStart, onReschedule }) => {
  const [marking, setMarking] = useState(false);
  const [reason, setReason] = useState('');
  const m = o.meeting;
  const shown = m && (isAhead(m) || isToday(m)) ? m : null;
  // Nothing on the books and their last one was missed (Nic 2026-10-30):
  // say so until a new meeting is booked.
  const missed = !shown && o.missedMeeting ? o.missedMeeting : null;
  const line = shown ? null : missed ? `They missed their call on ${moment(missed.startTime).format('ddd, MMM D [at] h:mm A')}. Book them again.` : 'No meeting booked yet.';
  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    const ok = await onStart(reason.trim());
    if (ok) {
      setMarking(false);
      setReason('');
    }
  };
  const title = (
    <span className="inline-flex shrink-0 items-center gap-[10px]">
      <span className={cn('h-[8px] w-[8px] shrink-0 rounded-full', shown ? 'bg-sky-500' : missed ? 'bg-rose-500' : 'bg-slate-300 dark:bg-slate-600')} />
      <span className="font-semibold text-slate-700 dark:text-slate-200">First meeting</span>
    </span>
  );
  const markButton = (
    <button type="button" onClick={() => setMarking(true)} className={STRIP_SECONDARY}>
      <Check size={13} /> Mark it happened
    </button>
  );
  return (
    // By its own width (Nic 2026-10-30, like the meeting's own row): wide,
    // the words and the buttons share a line; narrow, the buttons take a
    // row and spread evenly, and the words never squeeze.
    <div className={cn(STRIP, 'flex-wrap [container-type:inline-size]')}>
      {shown ? (
        // The meeting's own row: when, phone or video, its actions, and
        // Mark it happened at the end; a Cancel or Missed question opens
        // full width under it.
        <MeetingActions
          meeting={shown}
          mount="onboarding"
          leadId={onboardingId}
          canAct={canAct}
          onReschedule={onReschedule}
          label={title}
          extra={canAct ? markButton : null}
          hideActions={marking}
        />
      ) : (
        <>
          {title}
          <span className={cn('min-w-0 flex-1 basis-[14rem] leading-snug', missed ? 'text-rose-600 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400')}>{line}</span>
          {canAct && !marking ? (
            <span
              className={cn(
                'flex basis-full flex-wrap gap-[6px] [&>*]:flex-1 [&>*]:justify-center',
                '[@container(min-width:48rem)]:ml-auto [@container(min-width:48rem)]:basis-auto [@container(min-width:48rem)]:justify-end [@container(min-width:48rem)]:[&>*]:flex-none',
              )}
            >
              <button type="button" onClick={onBook} className={STRIP_PRIMARY}>
                <CalendarPlus size={13} /> Book their meeting
              </button>
              {markButton}
            </span>
          ) : null}
        </>
      )}
      {canAct && marking ? (
        <form onSubmit={submit} className="flex w-full flex-wrap items-center gap-[6px] pt-[2px]">
          <label htmlFor="first-meeting-reason" className="sr-only">
            Why it wasn&apos;t booked
          </label>
          <input
            id="first-meeting-reason"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason, e.g. They called in and we did it then"
            className="h-[30px] min-w-[14rem] flex-1 rounded-lg bg-white px-[10px] text-[13px] text-slate-700 outline-none ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-sky-400/60 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
          />
          <span className="text-[11px] tabular-nums text-slate-400">
            {reason.length}/{REASON_MAX}
          </span>
          <button type="submit" disabled={busy || !reason.trim()} className={STRIP_PRIMARY}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Mark it happened
          </button>
          <button
            type="button"
            onClick={() => {
              setMarking(false);
              setReason('');
            }}
            className={STRIP_SECONDARY}
          >
            Cancel
          </button>
        </form>
      ) : null}
    </div>
  );
};

// A meeting booked after their first call (in-app meetings, 2026-10-25):
// when it is, and Reschedule / Cancel / Missed / Join.
const NextMeetingStrip = ({ o, onboardingId, canAct, onReschedule }) => {
  const m = o.meeting;
  if (!m || !(isAhead(m) || isToday(m))) return null;
  return (
    <div className={STRIP}>
      <MeetingActions
        meeting={m}
        mount="onboarding"
        leadId={onboardingId}
        canAct={canAct}
        onReschedule={onReschedule}
        label={
          <span className="inline-flex shrink-0 items-center gap-[10px]">
            <span className="h-[8px] w-[8px] shrink-0 rounded-full bg-sky-500" />
            <span className="font-semibold text-slate-700 dark:text-slate-200">Next meeting</span>
          </span>
        }
      />
    </div>
  );
};

// Carried over at launch (Nic 2026-10-11): their claim was already In
// Process when the new checklist started, so they came straight to Ready
// for CM. Said plainly; Work the checklist moves them back into onboarding.
// They never see the checklist in their portal.
const CarriedStrip = ({ canMove, busy, onMove }) => (
  <div className={cn(STRIP, 'bg-amber-500/[0.06]')}>
    <History size={14} className="shrink-0 text-amber-600 dark:text-amber-300" />
    <span className="font-semibold text-slate-700 dark:text-slate-200">Carried over at launch</span>
    <span className="min-w-0 text-slate-500 dark:text-slate-400">
      In Process before this checklist, so they came straight to Ready for CM. They don&apos;t see it.
    </span>
    {canMove ? (
      <button type="button" disabled={busy} onClick={onMove} className={cn(STRIP_SECONDARY, 'ml-auto')}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Work the checklist
      </button>
    ) : null}
  </div>
);

const UndoFirstCall = ({ firstCall, busy, onUndo }) => {
  const [now, setNow] = useState(() => Date.now());
  const until = firstCall?.undoUntil ? new Date(firstCall.undoUntil).getTime() : 0;
  useEffect(() => {
    if (!until || until <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  if (!left) return null;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onUndo}
      className="inline-flex h-[24px] items-center gap-[5px] rounded-full bg-amber-50 px-[10px] text-[11.5px] font-semibold text-amber-800 outline-none ring-1 ring-amber-200 hover:bg-amber-100 focus:outline-none disabled:opacity-50 dark:!bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30"
    >
      <Undo2 size={12} /> Undo ({Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')})
    </button>
  );
};

const mmss = (ms) => {
  const t = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

// Their call, live (Nic 2026-10-10): the Sales workspace's pill, in this
// header. The call itself lives in the app's call center, so it keeps going
// when the Workspace closes or you move to another page (it docks bottom
// right; expanding the dock reopens them).
const LiveCallPill = ({ call, controls }) => (
  <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-emerald-50 py-1 pl-3.5 pr-1 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:!bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/30">
    <span className="relative flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-[0.75]" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
    </span>
    {call.dialing ? 'Calling…' : 'On the call'}
    <span className="tabular-nums text-emerald-700 dark:text-emerald-300">{mmss(controls.elapsedMs)}</span>
    <button
      type="button"
      onClick={controls.toggleMute}
      title={controls.muted ? 'Unmute' : 'Mute'}
      className={cn(
        'ml-1 flex h-8 w-8 items-center justify-center rounded-full outline-none transition-[background-color] focus:outline-none',
        controls.muted
          ? 'bg-amber-500 text-white'
          : 'bg-white text-emerald-700 hover:bg-emerald-100 dark:!bg-white/10 dark:text-emerald-200',
      )}
    >
      {controls.muted ? <MicOff size={15} /> : <Mic size={15} />}
    </button>
    <button
      type="button"
      onClick={controls.hangup}
      title="Hang up"
      className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white outline-none hover:bg-red-500 focus:outline-none"
    >
      <PhoneOff size={15} />
    </button>
  </span>
);

// ---- Checklist: the steps on the left, the open step on the right ---------------

// ---- Expedite (Nic 2026-10-26) ---------------------------------------------------
// A lightning icon in the header. Off: a quiet icon; a click opens the
// reasons, one click picks (Other asks for a few words). On: a rose pill
// saying why; a click changes or removes it. An Intent to File expiring
// within 90 days turns it on by itself (no Remove). CM Assignment puts them
// first.
export const EXPEDITE_REASONS = [
  { value: 'hardship', label: 'Hardship' },
  { value: 'health', label: 'Serious health' },
  { value: 'manager', label: 'Manager asked' },
  { value: 'other', label: 'Other' },
];
// A few words, not a story (Nic 2026-10-26).
const EXPEDITE_NOTE_MAX = 60;
export const expediteLine = (x) => {
  if (!x) return null;
  if (x.auto) {
    const when = x.itfExpires ? moment(x.itfExpires).format('MMM D, YYYY') : '';
    return /expired/i.test(x.label || '') ? `ITF expired ${when}` : `ITF expires ${when}`;
  }
  return x.label;
};

export const ExpediteButton = ({ expedite, canAct, busy, onSave, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!open) {
      setOther(false);
      setNote(expedite?.reason === 'other' ? expedite.note || '' : '');
    }
  }, [open, expedite]);
  if (!expedite && !canAct) return null;
  const pick = async (reason, n) => {
    if (await onSave({ reason, note: n })) setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {expedite ? (
          <button
            type="button"
            title={expedite.note || undefined}
            className="inline-flex h-[24px] max-w-full items-center gap-[4px] rounded-full bg-rose-500/10 px-[9px] text-[12px] font-semibold text-rose-700 outline-none hover:bg-rose-500/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60 dark:text-rose-300"
          >
            <Zap size={12} className="shrink-0" />
            <span className="truncate">Expedited · {expediteLine(expedite)}</span>
          </button>
        ) : (
          <button
            type="button"
            aria-label="Expedite"
            title="Expedite"
            className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-slate-400 outline-none ring-1 ring-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60 dark:ring-white/10 dark:hover:!bg-rose-500/10 dark:hover:text-rose-300"
          >
            <Zap size={13} />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[248px] p-[6px]" aria-label="Expedite">
        <div className="px-[8px] pb-[6px] pt-[4px]">
          <div className="flex items-center gap-[6px] text-[13px] font-bold text-slate-800 dark:text-slate-100">
            <Zap size={13} className="text-rose-500" /> Expedite
          </div>
          <div className="text-[11.5px] text-slate-500 dark:text-slate-400">Puts them first on CM Assignment.</div>
        </div>
        {expedite?.auto ? (
          <p className="mb-[4px] rounded-lg bg-rose-50 px-[8px] py-[7px] text-[12px] text-rose-800 dark:!bg-rose-500/10 dark:text-rose-200">
            Their Intent to File {/expired/i.test(expedite.label || '') ? 'expired' : 'expires'}{' '}
            {moment(expedite.itfExpires).format('MMM D, YYYY')}, so they&apos;re expedited on their own.
          </p>
        ) : canAct ? (
          <>
            <ul className="mb-0 list-none pl-0">
              {EXPEDITE_REASONS.map((r) => {
                const on = expedite?.reason === r.value;
                return (
                  <li key={r.value}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => (r.value === 'other' ? setOther(true) : pick(r.value, ''))}
                      className={cn(
                        'flex w-full items-center gap-[8px] rounded-lg px-[8px] py-[7px] text-left text-[13px] outline-none focus:outline-none disabled:opacity-50',
                        on || (r.value === 'other' && other)
                          ? 'bg-rose-50 font-semibold text-rose-800 dark:!bg-rose-500/10 dark:text-rose-200'
                          : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:!bg-white/5',
                      )}
                    >
                      <span className="flex-1">{r.label}</span>
                      {on ? <Check size={14} className="text-rose-600 dark:text-rose-300" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            {other ? (
              <div className="flex items-center gap-[6px] px-[4px] pb-[4px] pt-[2px]">
                <input
                  autoFocus
                  value={note}
                  maxLength={EXPEDITE_NOTE_MAX}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && note.trim()) pick('other', note.trim());
                  }}
                  placeholder="Why?"
                  className="h-[30px] min-w-0 flex-1 rounded-lg bg-white px-[8px] text-[13px] text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-rose-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
                />
                <button
                  type="button"
                  disabled={!note.trim() || busy}
                  onClick={() => pick('other', note.trim())}
                  className="h-[30px] rounded-lg bg-rose-600 px-[10px] text-xs font-semibold text-white outline-none hover:bg-rose-700 focus:outline-none disabled:opacity-50"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
                </button>
              </div>
            ) : null}
            {expedite ? (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  await onRemove();
                  setOpen(false);
                }}
                className="mt-[2px] w-full rounded-lg px-[8px] py-[7px] text-left text-[12.5px] font-semibold text-slate-500 outline-none hover:bg-slate-50 hover:text-slate-700 focus:outline-none disabled:opacity-50 dark:text-slate-400 dark:hover:!bg-white/5"
              >
                Remove
              </button>
            ) : null}
          </>
        ) : (
          <p className="mb-[4px] px-[8px] text-[12px] text-slate-500 dark:text-slate-400">
            {expedite.label}
            {expedite.note ? `: ${expedite.note}` : ''}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

const ChecklistTab = ({ data, answers, locked, active, onPick, onAnswer, onSaveAddress, savingAddress, files, strips = null, saCheckPanel = null, vaGovPanel = null, poaPanel = null, cmPrefsPanel = null }) => {
  const steps = data.steps || [];
  const step = steps.find((s) => s.id === active) || steps[0];
  const stepFiles = (files || []).filter((f) => f.fileMeta?.onboardingItem === step?.id);
  return (
    <div className="flex min-h-full flex-col">
    {strips}
    <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[17.5rem_minmax(0,1fr)]">
      <div className="border-b border-slate-100 dark:border-white/10 md:border-b-0 md:border-r">
      {/* No bar or count (Nic 2026-10-26): the list and the tab's badge say it. */}
      <ol className="mb-0 list-none py-[8px] pl-0">
        {steps.map((s, i) => {
          const on = s.id === step?.id;
          const Icon = STEP_ICON[s.id] || ClipboardCheck;
          // Verify POA (Nic 2026-10-27): they tapped I've done it, waiting
          // on the specialist's Confirm (a pulsing dot, like VA.gov's).
          const pill = s.id === 'poa' && data.poa?.stage === 'client_done' ? 'poaDone' : pillStatusOf(s, data.vaGov);
          // One marker per step, the square (Nic 2026-10-11: no "Client"
          // word beside it, no pill by the title). Green check: done. Purple
          // check: the service treatment record's FOIA is out (it counts).
          // Amber hourglass: the client is getting it. Grey dash: doesn't
          // apply. Otherwise its number, grey until it's done (the address
          // too: not added yet is to do, not a red X).
          // Confirm service agreement (Nic 2026-10-23): a pulsing dot while
          // the robot calls, amber when it needs a phone check.
          const sa = s.id === 'sa_confirmed' && s.status !== 'pass' ? data.saCheck : undefined;
          const saWorking = !!sa && ['queued', 'calling'].includes(sa.status);
          const saLook = s.id === 'sa_confirmed' && s.status !== 'pass' && (sa === null || (sa && !saWorking && sa.verdict !== 'match' && sa.verdict !== 'rating_only_match'));
          const mark = saWorking
            ? { tone: 'bg-sky-500/15 text-sky-600 dark:text-sky-300', icon: <Loader2 size={12} strokeWidth={2.5} className="animate-spin motion-reduce:animate-none" />, title: 'Checking with the VA' }
            : saLook
              ? { tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300', icon: <AlertTriangle size={12} strokeWidth={2.5} />, title: 'Needs a phone check' }
              : pill === 'na'
              ? { tone: 'bg-slate-100 text-slate-400 dark:!bg-white/5 dark:text-slate-500', icon: <Minus size={13} strokeWidth={3} />, title: "Doesn't apply" }
              : s.status === 'pass'
                ? { tone: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300', icon: <Check size={13} strokeWidth={3} />, title: 'Done' }
                : pill === 'requested'
                  ? { tone: 'bg-violet-500/15 text-violet-700 dark:text-violet-300', icon: s.followsWhenRequested ? <Check size={13} strokeWidth={3} /> : i + 1, title: 'Requested' }
                  : pill === 'client'
                    ? { tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300', icon: <Hourglass size={12} strokeWidth={2.5} />, title: 'Client is getting it' }
                    : on
                      ? { tone: 'bg-sky-500 text-white', icon: i + 1, title: 'To do' }
                      : { tone: 'bg-slate-100 text-slate-400 dark:!bg-white/5', icon: i + 1, title: 'To do' };
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onPick(s.id)}
                aria-current={on ? 'step' : undefined}
                className={cn(
                  'flex w-full items-center gap-[10px] px-[14px] py-[8px] text-left outline-none focus:outline-none',
                  on
                    ? 'bg-sky-50 dark:!bg-sky-500/10'
                    : 'hover:bg-slate-50 dark:hover:!bg-white/[0.03]',
                )}
              >
                <span
                  title={mark.title}
                  className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold', mark.tone)}
                >
                  {mark.icon}
                </span>
                <span
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-[6px] text-[13px]',
                    on ? 'font-semibold text-slate-900 dark:text-white' : 'font-medium text-slate-600 dark:text-slate-300',
                  )}
                >
                  <Icon size={14} className="shrink-0 opacity-50" />
                  {/* Checked by default for a carried-over client (Nic
                      2026-10-27): the step is only for new clients. */}
                  <span className={cn('leading-tight', isCarriedSa(s, data.answerMeta) && 'italic line-through opacity-70')}>{s.title}</span>
                </span>
                {pill === 'vaReady' || pill === 'vaWaiting' || pill === 'poaDone' ? (
                  <span className={cn('h-[7px] w-[7px] shrink-0 rounded-full bg-sky-500', (pill === 'vaReady' || pill === 'poaDone') && 'animate-pulse')} />
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
      </div>
      {step ? (
        <div className="min-w-0 px-[16px] pb-[16px] pt-[10px] xl:px-[22px]">
          <div className="flex flex-wrap items-center gap-[10px]">
            <h3 className="mb-0 text-base font-bold text-slate-800 dark:text-slate-100">{step.title}</h3>
          </div>
          {step.id === 'sa_confirmed' && saCheckPanel && !isCarriedSa(step, data.answerMeta) ? (
            saCheckPanel
          ) : step.id === 'va_gov' && vaGovPanel ? (
            vaGovPanel
          ) : step.id === 'poa' && poaPanel ? (
            poaPanel
          ) : step.id === 'cm_preferences' && cmPrefsPanel ? (
            cmPrefsPanel
          ) : (
          <StepDetail
            step={step}
            answers={answers}
            answerMeta={data.answerMeta || {}}
            vaGov={data.vaGov}
            signedAt={data.onboarding.signedAt}
            locked={locked}
            onAnswer={onAnswer}
            address={data.onboarding.address}
            savingAddress={savingAddress}
            onSaveAddress={onSaveAddress}
          />
          )}
          {stepFiles.length ? (
            <div className="mt-[18px]">
              <div className={cn(LABEL, 'mb-[6px]')}>Their files for this step</div>
              <ul className="mb-0 list-none space-y-[4px] pl-0">
                {stepFiles.map((f) => (
                  <li key={f.fileId}>
                    <FileOpen file={f} className="text-[13px]" />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
    </div>
  );
};

// ---- Files: every file on the claim, one flat list (Nic 2026-10-09) -------------

const openClaimFile = async (file) => {
  try {
    const res = await axios.get(`/claims/${file.claimId}/files/${file.fileId}/read`);
    if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
  } catch (e) {
    toast.error(e?.response?.data?.msg || "That file couldn't be opened.");
  }
};

const FileOpen = ({ file, className }) => (
  <button
    type="button"
    onClick={() => openClaimFile(file)}
    className={cn(
      'inline-flex max-w-full items-center gap-[6px] text-left font-medium text-sky-700 outline-none hover:underline focus:outline-none dark:text-sky-300',
      className,
    )}
  >
    <FileText size={14} className="shrink-0" />
    <span className="min-w-0 [overflow-wrap:anywhere]">{file.fileName}</span>
  </button>
);

// What a client's file is (Nic 2026-10-24): their "Something else" uploads
// say "Check what this is", and a step chip can be wrong. Either one opens
// the checklist's document steps plus "Not a checklist item"; picking a step
// files it there exactly as if they'd uploaded it on that step.
const NOT_AN_ITEM = 'none';
const LabelChip = ({ label, tone, options, onPick, busy }) => (
  <span className="inline-flex">
    <PickerMenu
      label={busy ? 'Saving…' : label}
      options={options}
      onPick={onPick}
      disabled={busy}
      title="What is this file?"
      menuMinWidth={264}
      menuClassName="rounded-xl"
      itemClassName="px-3 py-2 text-sm"
      triggerClassName={cn(
        'rounded-full px-[7px] py-px text-[11px] font-semibold outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
        tone === 'step'
          ? 'bg-indigo-500/10 text-indigo-700 hover:bg-indigo-500/20 dark:text-indigo-300'
          : 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300',
      )}
    />
  </span>
);

const FileRow = ({ file, clientUserId, stepTitle, onRenamed, canRename = true, itemOptions = null, onLabel = null }) => {
  const [labeling, setLabeling] = useState(false);
  const pickItem = async (item) => {
    if (!onLabel || item === file.fileMeta?.onboardingItem) return;
    setLabeling(true);
    try {
      await onLabel(file, item);
    } finally {
      setLabeling(false);
    }
  };
  const labelOptions = itemOptions ? [...itemOptions, { value: NOT_AN_ITEM, label: 'Not a checklist item' }] : null;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(file.fileName);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const next = name.trim();
    if (!next || next === file.fileName) {
      setEditing(false);
      setName(file.fileName);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/claims/${file.claimId}/files/${file.fileId}`, { fileName: next });
      toast.success('Renamed.');
      setEditing(false);
      onRenamed();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That name didn't save.");
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    setEditing(false);
    setName(file.fileName);
  };
  // Templates every claim gets (the How To PDFs, the agreement) are filed
  // under the client but were never uploaded by them.
  const ours = !!file.formId || /^Service_Agreement/i.test(file.fileName || '');
  const byThem = !ours && file.userId && file.userId === clientUserId;
  return (
    <li className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px] px-[16px] py-[10px] xl:px-[20px]">
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-[6px]">
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') {
                  // Backs out of the rename only, not the Workspace.
                  e.stopPropagation();
                  cancel();
                }
              }}
              className="h-8 min-w-0 flex-1 rounded-lg bg-white px-[10px] text-[13px] text-slate-700 outline-none ring-1 ring-sky-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-sky-400/40"
            />
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="inline-flex h-8 items-center rounded-lg bg-slate-900 px-[12px] text-xs font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:!bg-slate-100 dark:!text-slate-900"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
            </button>
            <button
              type="button"
              title="Cancel"
              aria-label="Cancel renaming"
              disabled={saving}
              onClick={cancel}
              className="rounded-md p-[6px] text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none disabled:opacity-50 dark:hover:!bg-white/10 dark:hover:text-white"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <FileOpen file={file} className="text-[13px]" />
        )}
        <div className="mt-[2px] flex flex-wrap items-center gap-[6px] text-[11px] text-slate-400">
          <span>{file.fileLastUpdated ? moment.utc(file.fileLastUpdated).format('MMM D, YYYY') : ''}</span>
          <span>·</span>
          <span>{byThem ? 'Uploaded by them' : ours ? 'Added by us' : 'Staff'}</span>
          {stepTitle ? (
            labelOptions ? (
              <LabelChip label={stepTitle} tone="step" options={labelOptions} onPick={pickItem} busy={labeling} />
            ) : (
              <span className="rounded-full bg-indigo-500/10 px-[7px] py-px font-semibold text-indigo-700 dark:text-indigo-300">
                {stepTitle}
              </span>
            )
          ) : byThem && file.fileMeta?.onboardingItem === 'other' && !file.fileMeta?.otherReviewed ? (
            // They picked "Something else" in their Documents tab (Nic
            // 2026-10-23: no typing for them; we look at it instead).
            labelOptions ? (
              <LabelChip label="Check what this is" tone="check" options={labelOptions} onPick={pickItem} busy={labeling} />
            ) : (
              <span className="rounded-full bg-amber-500/15 px-[7px] py-px font-semibold text-amber-700 dark:text-amber-300">
                Check what this is
              </span>
            )
          ) : null}
        </div>
      </div>
      {file.fileLocked ? (
        <span title="Locked" className="text-slate-400">
          <Lock size={14} />
        </span>
      ) : !editing && canRename ? (
        <button
          type="button"
          title="Rename"
          aria-label={`Rename ${file.fileName}`}
          onClick={() => setEditing(true)}
          className="rounded-md p-[6px] text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <Pencil size={14} />
        </button>
      ) : null}
    </li>
  );
};

// Upload for them (Nic 2026-10-10): the client sent their specialist a
// document directly (email, text) instead of uploading it. Pick the step it
// belongs to so it shows on that step, like the client's own uploads.
const UploadForThem = ({ claimId, steps, onUploaded }) => {
  const input = React.useRef(null);
  const [item, setItem] = useState('');
  const { busy, current, total, startUpload } = useFileUpload(claimId, () => {});
  const docSteps = steps.filter((st) => !st.auto && !st.staffOnly);
  return (
    <div className="flex flex-wrap items-center gap-[8px] border-b border-slate-100 px-[14px] py-[10px] dark:border-white/10">
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={async (e) => {
          // Copy before the reset: in Chrome, clearing the input empties the
          // same FileList object, so a live reference reads as no files and
          // the upload never started (Josue 2026-11-02).
          const list = Array.from(e.target.files || []);
          e.target.value = '';
          if (!list?.length) return;
          try {
            const { uploaded, failed } = await startUpload(list, null, null, {
              ...(item && item !== 'other' ? { onboardingItem: item } : {}),
              uploadedForClient: true,
            });
            if (uploaded && !failed) toast.success(uploaded === 1 ? 'Uploaded.' : `${uploaded} files uploaded.`);
            else if (failed) toast.error(`${failed} didn't upload. Please try again.`);
            onUploaded();
          } catch (err) {
            toast.error(err?.response?.data?.msg || "That upload didn't go through.");
          }
        }}
      />
      <span className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">Got a file from them?</span>
      {/* Pick what it is first (Nic 2026-10-10); Other at the bottom. */}
      <Picker
        value={item}
        placeholder="What is it?"
        options={[
          ...docSteps.map((st) => ({ value: st.id, label: st.title })),
          { value: 'other', label: 'Other' },
        ]}
        onPick={setItem}
      />
      <button
        type="button"
        disabled={busy || !item}
        title={item ? undefined : 'Pick what it is first'}
        onClick={() => input.current?.click()}
        className="inline-flex h-[32px] items-center gap-[6px] rounded-lg bg-slate-900 px-[12px] text-[13px] font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-white dark:!text-slate-900"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {busy ? `Uploading${total > 1 ? ` ${current} of ${total}` : ''}...` : 'Upload for them'}
      </button>
    </div>
  );
};

// canRename: not while they're stalled (read only, Nic 2026-10-12).
const FilesTab = ({
  files,
  isLoading,
  clientUserId,
  stepTitles,
  onRenamed,
  upload = null,
  canRename = true,
  itemOptions = null,
  onLabel = null,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={18} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (!files.length) {
    return (
      <>
        {upload}
        <div className="py-12 text-center text-sm text-slate-400">No files on their claim yet.</div>
      </>
    );
  }
  return (
    <>
    {upload}
    <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/10">
      {files.map((f) => (
        <FileRow
          key={f.fileId}
          file={f}
          clientUserId={clientUserId}
          stepTitle={stepTitles[f.fileMeta?.onboardingItem] || null}
          onRenamed={onRenamed}
          canRename={canRename}
          itemOptions={itemOptions}
          onLabel={onLabel}
        />
      ))}
    </ul>
    </>
  );
};

// ---- Their Service Agreement (Nic 2026-10-09) ------------------------------------
// A signed agreement that needs changing is Onboarding's (Signups is gone):
// the same Facts and Agreement sections as Sales, on Client Onboarding's
// owner-only routes. "Correct & resend" lives in the Agreement section.
const AgreementPanel = ({ onboardingId, autoAsk = null, onAutoAsked = null }) => {
  const q = useClientAgreement(onboardingId);
  const queryClient = useQueryClient();
  const [factsDirty, setFactsDirty] = useState(false);
  const api = useMemo(() => onboardingAgreementApiFor(onboardingId), [onboardingId]);
  const refresh = () => {
    queryClient.invalidateQueries(['client-onboarding', 'agreement', onboardingId]);
    queryClient.invalidateQueries(['client-onboarding', 'client', onboardingId]);
  };
  const row = q.data?.row;
  const canAct = !!q.data?.canAct;
  return (
    <section>
      <div className="space-y-[12px]">
        {q.isLoading ? (
          <Loader2 size={16} className="animate-spin text-slate-400" />
        ) : !row ? (
          <div className="text-xs text-slate-400">No agreement details on file.</div>
        ) : (
          <>
            {q.data?.lockedWhy ? (
              // Only up to With a CM (Nic 2026-10-23), like Signed SAs.
              <p className="mb-0 rounded-xl bg-sky-50 px-3 py-2.5 text-xs font-medium text-sky-800 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/30">
                {q.data.lockedWhy}
              </p>
            ) : null}
            {/* The facts only matter while a correction is under way (Nic
                2026-10-09): a signed agreement showed a "waiting for them"
                banner over locked, blank facts. Correct & Resend opens them. */}
            {row.userIntakeCorrectionStartedAt ? (
              <FactsSection
                key={`facts-${row.userId}-${row.userIntakeConfirmedAt || 'none'}-${row.userIntakeCorrectionStartedAt || ''}`}
                api={api}
                row={row}
                claimedByMe={canAct}
                onConfirmed={refresh}
                onDirtyChange={setFactsDirty}
                plain
                onboarding
              />
            ) : null}
            <AgreementSection
              api={api}
              row={row}
              claimedByMe={canAct}
              factsDirty={factsDirty}
              onChanged={refresh}
              plain
              autoAsk={autoAsk}
              onAutoAsked={onAutoAsked}
              // View PDF, the same as Signed SAs (Nic 2026-10-23).
              signedPdf={q.data?.signedPdf ?? null}
              onOpenSignedPdf={openSignedPdf}
            />
          </>
        )}
      </div>
    </section>
  );
};

// ---- Manage: the agreement, hand them on, or Move to Dead (asked once, in place) --

// greyed (Nic 2026-10-23): someone who isn't the owner sees Reassign and
// Move to Dead greyed out, never missing.
// intent { card, ask, n } (Nic 2026-10-23): the VA check's Correct & resend
// lands here with that card open (and its confirm showing).
// onIntentDone: the confirm has shown, so the Workspace drops the ask (Cancel
// correction, reopening the card or coming back to the tab never re-asks).
const ManageTab = ({ o, onboardingId, canHandOff, canKill, greyed = false, onClose, intent = null, onIntentDone = null }) => {
  const reassign = useReassignClient();
  const markDead = useMarkDead();
  const specialists = useSpecialists(canHandOff);
  const [toUserId, setToUserId] = useState('');
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [armed, setArmed] = useState(false);
  useEffect(() => setArmed(false), [reason, other]);
  const doReassign = async () => {
    try {
      await reassign.mutateAsync({ onboardingId, toUserId });
      toast.success('Reassigned.');
      setToUserId('');
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };
  const doDead = async () => {
    try {
      await markDead.mutateAsync({
        onboardingId,
        reason,
        reasonDetail: reason === 'Other' ? other.trim() : undefined,
      });
      toast.success('Moved to Dead. Their claim is Dead now.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
      setArmed(false);
    }
  };
  // Rows, one under another, like the Sales Workspace's Manage (Nic
  // 2026-10-21); each opens its form, and opening one closes the others.
  const [open, setOpen] = useState(intent?.card || null);
  const agreementRef = React.useRef(null);
  useEffect(() => {
    if (!intent?.card) return;
    setOpen(intent.card);
    // After the tab paints, bring the card into view.
    requestAnimationFrame(() => agreementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    // Once per Correct & resend (n), not on every intent change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.card, intent?.n]);
  const toggle = (key) => {
    setArmed(false);
    setOpen((k) => (k === key ? null : key));
  };
  const row = (key) => ({ open: open === key, dimmed: !!open && open !== key, onToggle: () => toggle(key) });
  const deadReady = !!reason && (reason !== 'Other' || !!other.trim());
  return (
    <div className="space-y-2">
      <div ref={agreementRef} className="scroll-mt-[12px]">
      <ManageCard
        icon={FileSignature}
        tone="emerald"
        title="Service agreement"
        blurb="Something on it needs changing? Correct the facts and send a new one. The signed one stands until they sign it."
        {...row('agreement')}
      >
        <AgreementPanel
          onboardingId={onboardingId}
          autoAsk={intent?.card === 'agreement' && intent.ask ? intent : null}
          onAutoAsked={onIntentDone}
        />
      </ManageCard>
      </div>

      {canHandOff || greyed ? (
        <ManageCard
          icon={UserCheck}
          tone="indigo"
          title="Give them to another specialist"
          blurb="They move to that specialist with everything they've done."
          {...row('reassign')}
          footer={
            <button
              type="button"
              disabled={!canHandOff || !toUserId || reassign.isLoading}
              title={canHandOff ? undefined : VIEW_ONLY_TITLE}
              onClick={doReassign}
              className="w-full whitespace-nowrap rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900 dark:hover:!bg-white"
            >
              Reassign
            </button>
          }
        >
          <div className="[&>*:last-child]:w-full">
            <ManageLabel>Move them to</ManageLabel>
            <Picker
              value={toUserId}
              placeholder="Pick a specialist"
              options={(specialists.data?.specialists || [])
                .filter((sp) => sp.userId !== o.ownerUserId)
                .map((sp) => ({ value: sp.userId, label: sp.name }))}
              onPick={setToUserId}
              disabled={!canHandOff}
            />
          </div>
        </ManageCard>
      ) : null}

      {canKill || greyed ? (
        <ManageCard
          icon={Skull}
          tone="rose"
          title="Move to Dead"
          blurb="Not going forward. Signed clients have no Stalled or Drop by hand: their claim moves to Dead."
          {...row('dead')}
          footer={
            armed ? (
              <div className="flex items-center gap-2">
                <span className="mr-auto text-sm font-semibold text-rose-700 dark:text-rose-300">
                  Move {o.firstName || 'them'} to Dead?
                </span>
                <button
                  type="button"
                  onClick={() => setArmed(false)}
                  className="whitespace-nowrap rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-600 outline-none hover:bg-slate-200 focus:outline-none dark:!bg-white/10 dark:text-slate-300 dark:hover:!bg-white/15"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={markDead.isLoading}
                  onClick={doDead}
                  className="inline-flex min-w-[88px] items-center justify-center whitespace-nowrap rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white outline-none hover:bg-rose-500 focus:outline-none disabled:opacity-40"
                >
                  {markDead.isLoading ? <Loader2 size={14} className="animate-spin" /> : 'Yes, Dead'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={!canKill || !deadReady}
                title={canKill ? undefined : VIEW_ONLY_TITLE}
                onClick={() => setArmed(true)}
                className="w-full whitespace-nowrap rounded-xl bg-rose-600 py-2.5 text-sm font-semibold text-white outline-none transition-[background-color] hover:bg-rose-500 focus:outline-none disabled:opacity-40"
              >
                Move to Dead
              </button>
            )
          }
        >
          <div className="[&>*:last-child]:w-full">
            <ManageLabel>Why?</ManageLabel>
            <Picker
              value={reason}
              placeholder="Pick a reason"
              options={ONBOARDING_DEAD_REASONS.map((r) => ({ value: r, label: r }))}
              onPick={setReason}
              disabled={!canKill}
            />
          </div>
          {reason === 'Other' ? (
            <div>
              <input
                value={other}
                maxLength={DEAD_OTHER_MAX}
                onChange={(e) => setOther(e.target.value)}
                placeholder="What's the reason?"
                className="h-9 w-full rounded-lg bg-white px-[12px] text-sm text-slate-700 outline-none ring-1 ring-slate-200 focus:outline-none focus:ring-slate-300 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
              />
              <div className="mt-[2px] text-right text-[10px] tabular-nums text-slate-400">
                {other.length}/{DEAD_OTHER_MAX}
              </div>
            </div>
          ) : null}
        </ManageCard>
      ) : null}
    </div>
  );
};

// ---- The Workspace ---------------------------------------------------------------

const ClientWorkspace = ({
  onboardingId,
  canManage,
  onClose,
  startComposer = false,
  openOnCall = false,
  initialTab = null,
  canReengage = false,
  // { canAct, isManager } from the board (Nic 2026-10-23): admins and
  // viewers act on nothing; the manager moves anyone, works only his own.
  viewer = null,
}) => {
  const { user, hasPermission } = useUser();
  const queryClient = useQueryClient();
  const q = useClientOnboardingClient(onboardingId);
  const setAnswer = useSetOnboardingAnswer();
  const setAddress = useSetOnboardingAddress();
  const sendText = useSendOnboardingText();
  const sendBack = useSendBackToVaGov();
  const expediteM = useExpedite();
  const workChecklist = useWorkChecklist();
  const acceptSa = useAcceptSa();
  const startSa = useStartSaCheck();
  const startCall = useStartFirstCall();
  const undoCall = useUndoFirstCall();
  const transcripts = useLeadTranscripts('/client-onboarding', onboardingId);
  const reengage = useAdminReengage();
  const [reengaging, setReengaging] = useState(false);
  const [reengageReason, setReengageReason] = useState('');
  const [reengageTo, setReengageTo] = useState('');
  const reengageSpecialists = useSpecialists(!!viewer?.isManager);
  const [tab, setTab] = useState(initialTab || 'checklist');
  // Where Manage should land (the VA check's Correct & resend). Leaving the
  // tab drops it, so coming back finds every card collapsed (Nic 2026-10-23).
  const [manageIntent, setManageIntent] = useState(null);
  useEffect(() => {
    if (tab !== 'manage') setManageIntent(null);
  }, [tab]);
  const [activeStep, setActiveStep] = useState(null);
  // The text composer (Nic 2026-10-27): in here it is always Text and opens
  // empty ('text'); the nudge with its wording lives only on the desk rows
  // ('nudge', kept for startComposer callers), false is closed.
  const [composer, setComposer] = useState(false);
  // Calls run through the app's call center (Nic 2026-10-10).
  const callCenter = useCallCenter();
  const liveCall =
    callCenter.active?.client?.onboardingId === onboardingId ? callCenter.active : null;
  // While this Workspace is open, its header carries the call (no dock).
  const { setClientPanel } = callCenter;
  useEffect(() => {
    if (!setClientPanel || !onboardingId) return undefined;
    setClientPanel(onboardingId);
    return () => setClientPanel((cur) => (cur === onboardingId ? null : cur));
  }, [setClientPanel, onboardingId]);
  const [booking, setBooking] = useState(false);
  // The meeting being moved (Reschedule), or null.
  const [moving, setMoving] = useState(null);
  const [confirmCall, setConfirmCall] = useState(false);
  const data = q.data;
  const o = data?.onboarding;
  const claimId = o?.claimId;

  // Answers show the moment they are clicked; the server's reply (and the
  // refetch) settles the step statuses.
  const [pending, setPending] = useState({});
  useEffect(() => setPending({}), [onboardingId, data?.answers]);
  const answers = useMemo(() => ({ ...(data?.answers || {}), ...pending }), [data?.answers, pending]);

  const files = useQuery(
    ['claim-files', claimId],
    async () => (await axios.get(`/claims/${claimId}/files`)).data,
    { enabled: !!claimId, refetchOnWindowFocus: true },
  );
  const fileList = useMemo(
    () =>
      [...(Array.isArray(files.data) ? files.data : [])].sort(
        (a, b) =>
          String(b.fileLastUpdated || '').localeCompare(String(a.fileLastUpdated || '')) ||
          String(a.fileName).localeCompare(String(b.fileName)),
      ),
    [files.data],
  );

  useEffect(() => {
    setTab(initialTab || 'checklist');
    setActiveStep(null);
    setComposer(false);
    setBooking(false);
    setMoving(null);
    setReengaging(false);
    setReengageReason('');
    // A new client resets the room; initialTab only picks where it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingId]);
  useEffect(() => {
    if (startComposer && data?.onboarding) setComposer('text');
    // Today's Call (Nic 2026-10-11): open on the call confirm.
    if (openOnCall && data?.onboarding?.phone) setConfirmCall(true);
    // Once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startComposer, openOnCall, onboardingId, !!data?.onboarding]);
  // The first step that still needs work opens first.
  useEffect(() => {
    if (activeStep || !data?.steps?.length) return;
    setActiveStep((data.steps.find((s) => s.status !== 'pass') || data.steps[0]).id);
  }, [data?.steps, activeStep]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || composer || booking || moving || confirmCall) return;
      // Escape closes the innermost thing only (QA 2026-10-13): a dialog on
      // top of the Workspace, then the re-engage reason, then the Workspace.
      if (document.querySelector('[data-ws-modal]')) return;
      // A popover or picker menu owns this Escape (QA 2026-10-26): Radix
      // marks the key handled when it closes one, and a PickerMenu is still
      // open when this runs. The reschedule picker closes itself too.
      if (e.defaultPrevented) return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      if (document.querySelector('[aria-haspopup="listbox"][aria-expanded="true"]')) return;
      if (reengaging) {
        setReengaging(false);
        setReengageReason('');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [composer, booking, moving, confirmCall, reengaging, onClose]);

  if (!onboardingId) return null;

  if (q.isLoading || !data) {
    return createPortal(
      <WorkspaceFrame>
        <div className="flex flex-1 items-center justify-center">
          {q.isError ? (
            <div className="text-center">
              <div className="text-sm text-rose-500">{q.error?.response?.data?.msg || "That client couldn't be loaded."}</div>
              <button type="button" onClick={onClose} className="mt-3 text-xs font-semibold text-slate-500 underline">
                Close
              </button>
            </div>
          ) : (
            <Loader2 size={22} className="animate-spin text-slate-400" />
          )}
        </div>
      </WorkspaceFrame>,
      document.body,
    );
  }

  const va = data.vaGov || {};
  const vaWaiting = va.stage === 'waiting';
  const vaReady = va.stage === 'ready';
  const viewOnly = viewer?.canAct === false;
  const isManager = viewer ? !!viewer.isManager : !!canManage;
  const isMine = !viewOnly && isOwnedBy(o.ownerUserId, user?.userId);
  const withCm = o.state === 'with_cm';
  const inNurture = o.state === 'nurture';
  const readyForCm = o.state === 'ready_for_cm';
  const isDead = o.state === 'dead';
  const locked = withCm || inNurture || isDead || !isMine;
  const canHandOff = !!o && !withCm && !inNurture && !isDead && (isManager || isMine);
  const canKill = !withCm && !isDead && isMine;
  const canAct = isMine && !withCm && !inNurture && !isDead;
  // Someone else's live client: every action shows, greyed (2026-10-23).
  const greyed = !isMine && !withCm && !inNurture && !isDead;
  const steps = data.steps || [];
  const done = steps.filter((s) => s.status === 'pass').length;
  const fieldLabels = Object.fromEntries(steps.flatMap((st) => st.fields.map((f) => [f.id, f.label])));
  const stepTitles = Object.fromEntries(steps.map((s) => [s.id, s.title]));
  // Before their first call there is no checklist yet (round 3 step 5).
  const beforeCall = o.column === 'needsBooking' || o.column === 'booked';
  const undoFirstCall = async () => {
    try {
      await undoCall.mutateAsync({ onboardingId });
      toast.success(`${o.firstName || 'They'} ${o.firstName ? 'is' : 'are'} back in ${o.meeting ? 'Booked' : 'Needs booking'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };
  const startFirstCall = async (reason) => {
    try {
      await startCall.mutateAsync({ onboardingId, reason });
      setActiveStep(null);
      toast.success(
        ({ closeToast }) => (
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1">Marked as happened. They&apos;re in Gathering records.</span>
            <button
              type="button"
              onClick={() => {
                closeToast();
                undoFirstCall();
              }}
              className="shrink-0 rounded-md px-2 py-1 text-xs font-bold underline"
            >
              Undo
            </button>
          </div>
        ),
        { autoClose: 8000 },
      );
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
      return false;
    }
  };

  const run = async (fn, okText) => {
    try {
      await fn();
      if (okText) toast.success(okText);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };
  // Expedite (Nic 2026-10-26), from the header.
  const saveExpedite = async ({ reason, note }) => {
    try {
      await expediteM.mutateAsync({ onboardingId, on: true, reason, note });
      toast.success('Expedited. CM Assignment puts them first.');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
      return false;
    }
  };
  const answer = (fieldId, value) => {
    setPending((p) => ({ ...p, [fieldId]: value }));
    setAnswer
      .mutateAsync({ onboardingId, fieldId, value })
      .then((res) => {
        if (res?.state === 'ready_for_cm' && o.state !== 'ready_for_cm') {
          toast.success('Everything is in. They moved to Ready for CM.');
        }
      })
      .catch((e) => {
        setPending((p) => {
          const next = { ...p };
          delete next[fieldId];
          return next;
        });
        toast.error(e?.response?.data?.msg || "That answer didn't save.");
      });
  };

  const transcriptCount = transcripts.data?.transcripts?.length || 0;
  const tabs = [
    { key: 'checklist', label: 'Checklist', icon: ClipboardCheck, tone: 'sky', badge: beforeCall ? null : `${done}/${steps.length}` },
    { key: 'files', label: 'Files', icon: FolderOpen, tone: 'indigo', badge: fileList.length || null },
    // One row from the left (Nic 2026-10-12): Manage last.
    { key: 'transcripts', label: 'Transcripts', icon: AudioLines, tone: 'rose', badge: transcriptCount || null },
    { key: 'notes', label: 'Notes', icon: StickyNote, tone: 'amber', badge: o.notes?.trim() ? '1' : null },
    { key: 'manage', label: 'Manage', icon: Settings2, tone: 'violet' },
  ];
  const tone = tabs.find((t) => t.key === tab)?.tone || 'sky';

  // ---- The footer's one status line ----
  const status = (() => {
    if (withCm) {
      return { icon: Check, tone: 'slate', text: 'Their Claims Manager has them now.', sub: 'This is the record of their onboarding.' };
    }
    if (isDead) return { icon: Skull, tone: 'rose', text: 'Moved to Dead.', sub: 'Their claim is Dead.' };
    if (inNurture) {
      return {
        icon: Hourglass,
        tone: 'slate',
        text: 'Stalled.',
        sub: 'They come back on their own when they text, call, upload or sign in. Contact details stay hidden until then.',
        // Anyone who works the page brings them back by hand, with a reason
        // (Nic 2026-10-12, like Sales).
        // Admins and viewers see it greyed (Nic 2026-10-23).
        action:
          canReengage && !reengaging
            ? { label: 'Re-engage', icon: RotateCcw, onClick: () => setReengaging(true) }
            : viewOnly
              ? { label: 'Re-engage', icon: RotateCcw, disabled: true, title: VIEW_ONLY_TITLE }
              : null,
      };
    }
    if ((o.ownerUserId || viewOnly) && !isMine) return { notYours: true };
    if (readyForCm && o.carriedOver) {
      return {
        icon: History,
        tone: 'amber',
        text: 'Carried over at launch.',
        sub: 'Ready for a Claims Manager as is. Their Claims Manager is picked on CM Assignment.',
        action: hasPermission('cm_assignment:manage') ? { label: 'Open CM Assignment', href: '/operations/cm-assignment' } : null,
      };
    }
    if (readyForCm) {
      return {
        icon: Check,
        tone: 'emerald',
        text: 'Everything is in. Ready for CM.',
        sub: "Waiting on CM Assignment. You're still their point of contact until a Claims Manager is assigned.",
        action: hasPermission('cm_assignment:manage') ? { label: 'Open CM Assignment', href: '/operations/cm-assignment' } : null,
      };
    }
    if (vaReady && va.checkedNo) {
      return {
        icon: AlertTriangle,
        tone: 'amber',
        text: "VA.gov wasn't set up on the call.",
        sub: 'Recommended: send them back to Setting up VA.gov.',
        action: isMine
          ? {
              label: 'Send them back',
              icon: Undo2,
              busy: sendBack.isLoading,
              onClick: () =>
                run(
                  () => sendBack.mutateAsync({ onboardingId }),
                  `${o.firstName || 'They'} ${o.firstName ? 'is' : 'are'} back in Setting up VA.gov.`,
                ),
            }
          : null,
      };
    }
    if (vaWaiting) {
      return {
        icon: Hourglass,
        tone: 'sky',
        text: 'Setting up VA.gov: their step.',
        sub: `${va.bouncedAt ? `Not set up when checked on ${shortDate(va.bouncedAt)}. ` : ''}${o.stallOn ? `Goes to Stalled ${moment(o.stallOn).format('ddd, MMM D')} unless they reply or finish VA.gov. A nudge from us doesn't count.` : 'They stall with no sign of life, like everyone. A nudge from us doesn\'t count.'}${va.nudgedAt ? ` Last nudged ${shortDate(va.nudgedAt)}.` : ''}`,
        // No Nudge them here (Nic 2026-10-27): the nudge is the desk row's
        // icon; in here the header's Text opens empty.
        action: null,
      };
    }
    // No "Next: <step>" line (Nic 2026-10-10): the checklist shows what's
    // left. What shows is their last movement, the one aging signal (Nic
    // 2026-10-11), and when two weeks of none stalls them.
    if (o.state !== 'working') return null;
    const d = o.movementDays;
    const ago = d == null ? 'No movement yet.' : d === 0 ? 'Last movement: today.' : `Last movement: ${d} day${d === 1 ? '' : 's'} ago.`;
    return {
      icon: History,
      tone: d >= 14 ? 'rose' : d >= 7 ? 'amber' : 'slate',
      text: ago,
      // Signs of life only (Nic 2026-10-29): our texts, emails and
      // unanswered calls never push Stalled back.
      sub: o.stallOn
        ? `Goes to Stalled ${moment(o.stallOn).format('ddd, MMM D')} with no sign of life from them. A reply, an answered call, an upload or a step counts; our own texts and calls don't.`
        : 'A reply, an answered call, an upload or a step counts; our own texts and calls don\'t.',
      // No Log a call (Nic 2026-10-12): every call with them, from the page
      // or the RingCentral app, is found in the call log on its own.
    };
  })();
  const STATUS_TONE = {
    emerald: 'bg-emerald-500 text-white',
    amber: 'bg-amber-500 text-white',
    sky: 'bg-sky-500 text-white',
    rose: 'bg-rose-500 text-white',
    violet: 'bg-violet-500 text-white',
    slate: 'bg-slate-300 text-slate-700 dark:!bg-white/15 dark:text-slate-200',
  };

  return createPortal(
    <WorkspaceFrame z="z-[1060]">
      <header
        aria-label="Client Workspace"
        className="flex flex-wrap items-start gap-x-[16px] gap-y-[12px] border-b border-slate-200/80 bg-white px-[20px] py-[12px] dark:border-white/10 dark:!bg-surface-elevated lg:items-center lg:py-[10px] md:rounded-t-3xl"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white dark:!bg-slate-100 dark:!text-slate-900">
          {initialsOf(o.name)}
        </span>
        <div className="order-[1] min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-[12px] gap-y-[2px]">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <h2 className="mb-0 truncate text-base font-bold text-slate-800 dark:text-slate-100">{o.name || 'Client'}</h2>
              {/* They came in hot (Nic 2026-10-23). */}
              <HotSeatMark seat={o.hotSeatCard} size={14} />
              {/* Expedite (Nic 2026-10-26): an icon until it's on. */}
              {!withCm && !isDead ? (
                <ExpediteButton
                  expedite={o.expedite}
                  canAct={canAct}
                  busy={expediteM.isLoading}
                  onSave={saveExpedite}
                  onRemove={() => run(() => expediteM.mutateAsync({ onboardingId, on: false }), 'No longer expedited.')}
                />
              ) : null}
            </span>
            {/* No way to their claim from here (Nic 2026-10-12): everything
                onboarding needs is in this Workspace. */}
            {canAct && o.firstCall ? (
              <UndoFirstCall firstCall={o.firstCall} busy={undoCall.isLoading} onUndo={undoFirstCall} />
            ) : null}
          </div>
          <div className="mt-[2px] truncate text-[12px] text-slate-500 dark:text-slate-400">
            {[
              o.ownerName ? `${o.ownerName}'s client` : 'No specialist yet',
              data.lead?.salesRep ? `signed by ${data.lead.salesRep}` : null,
              o.partnerName || null,
              o.signedAt ? `day ${moment().startOf('day').diff(moment(o.signedAt).startOf('day'), 'days') + 1} since signing` : null,
              // How their first call counted (Nic 2026-11-03: a hot transfer
              // that connected is it, so nobody reaches for Mark it happened).
              o.firstCall
                ? `first call ${FIRST_CALL_VIA[o.firstCall.via] || ''}${moment(o.firstCall.at).format('MMM D [at] h:mm A')}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <PipelineTrack o={o} va={va} done={done} total={steps.length} />
        </div>
        {canAct || greyed ? (
          <div className="order-[3] flex basis-full items-center gap-2 max-lg:[&>*]:flex-1 max-lg:[&>*]:justify-center lg:order-[2] lg:ml-auto lg:basis-auto">
            {/* Text opens empty; Nudge (while VA.gov is their step) opens
                their nudge wording (Nic 2026-10-27). */}
            <TextButton
              onClick={() => setComposer('text')}
              label="Text"
              disabled={!canAct}
              title={canAct ? undefined : VIEW_ONLY_TITLE}
            />
            {liveCall ? (
              <LiveCallPill call={liveCall} controls={callCenter.callControls} />
            ) : (
              <CallButton
                name={o.name || 'them'}
                phone={o.phone}
                open={confirmCall}
                onOpenChange={setConfirmCall}
                disabled={!canAct || !o.phone || !!callCenter.active}
                title={
                  !canAct ? VIEW_ONLY_TITLE : callCenter.active ? 'Finish the current call first' : undefined
                }
                onCall={() =>
                  callCenter.startClientCall?.({
                    onboardingId,
                    firstName: o.firstName || '',
                    lastName: (o.name || '').replace(o.firstName || '', '').trim(),
                    phone: o.phone,
                  })
                }
              />
            )}
            {/* Booking never waits on VA.gov (Nic 2026-10-13, as the server). */}
            <button
              type="button"
              onClick={() => setBooking(true)}
              disabled={!canAct}
              title={canAct ? undefined : VIEW_ONLY_TITLE}
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-slate-100 px-3.5 text-sm font-semibold text-slate-700 outline-none hover:bg-slate-200 focus:outline-none disabled:opacity-50 dark:!bg-white/10 dark:text-slate-200 dark:hover:!bg-white/15"
            >
              <CalendarPlus size={14} /> Book
            </button>
          </div>
        ) : null}
        <div className="order-[2] flex items-center self-start lg:order-[3] lg:self-center">
          <button
            type="button"
            onClick={onClose}
            title={liveCall ? 'Minimize. The call keeps going' : 'Close'}
            aria-label={liveCall ? 'Minimize' : 'Close'}
            className="rounded-lg p-2 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-600 focus:outline-none dark:hover:!bg-white/10"
          >
            {liveCall ? <Minus size={18} /> : <X size={18} />}
          </button>
        </div>
      </header>

      <div className="os-scroll grid min-h-0 flex-1 auto-rows-max grid-cols-[minmax(0,1fr)] content-start gap-[12px] overflow-y-auto p-[12px] lg:auto-rows-auto lg:grid-cols-[20rem_minmax(0,1fr)] lg:content-stretch lg:overflow-hidden xl:grid-cols-[23rem_minmax(0,1fr)] xl:gap-[16px] xl:p-[16px]">
        <AboutPanel o={o} lead={data.lead} events={data.events || []} fieldLabels={fieldLabels} identity={data.identity} />
        <section className="flex min-h-0 flex-col">
          <FolderTabs tabs={tabs} active={tab} onSelect={setTab} label="Their onboarding" />
          <TabPage tone={tone} inner={['manage', 'transcripts', 'notes'].includes(tab)}>
            {tab === 'checklist' ? (
              <ChecklistTab
                strips={
                  <>
                    {o.carriedOver ? (
                      <CarriedStrip
                        canMove={isMine || isManager}
                        busy={workChecklist.isLoading}
                        onMove={() =>
                          run(
                            () => workChecklist.mutateAsync({ onboardingId }),
                            `${o.firstName || 'They'} ${o.firstName ? 'is' : 'are'} back in Gathering records.`,
                          )
                        }
                      />
                    ) : null}
                    {beforeCall ? (
                      <CallStrip
                        o={o}
                        onboardingId={onboardingId}
                        canAct={canAct}
                        busy={startCall.isLoading}
                        onBook={() => setBooking(true)}
                        onStart={startFirstCall}
                        onReschedule={setMoving}
                      />
                    ) : (
                      <NextMeetingStrip o={o} onboardingId={onboardingId} canAct={canAct} onReschedule={setMoving} />
                    )}
                  </>
                }
                // VA.gov access (Nic 2026-10-25): one Confirm, a short history,
                // and the way back, all inside the step (the line above the
                // checklist is gone).
                vaGovPanel={
                  <VaGovStep
                    va={va}
                    firstName={o.firstName}
                    confirmed={answers.va_gov_access === 'yes'}
                    confirmedMeta={data.answerMeta?.va_gov_access || null}
                    events={data.events || []}
                    canAct={isMine && !locked}
                    saving={setAnswer.isLoading}
                    sendingBack={sendBack.isLoading}
                    onConfirm={() => answer('va_gov_access', 'yes')}
                    onUndo={() => answer('va_gov_access', null)}
                    onSendBack={async () => {
                      try {
                        await sendBack.mutateAsync({ onboardingId });
                        toast.success(`${o.firstName || 'They'} ${o.firstName ? 'is' : 'are'} back in Setting up VA.gov.`);
                        return true;
                      } catch (e) {
                        toast.error(e?.response?.data?.msg || "That didn't go through.");
                        return false;
                      }
                    }}
                  />
                }
                // Verify POA (Nic 2026-10-27): Yes / No on the call, then
                // Confirm or send it back once they say it's removed. Every
                // write is a checklist answer.
                poaPanel={
                  <PoaStep
                    poa={data.poa || null}
                    step={(data.steps || []).find((s) => s.id === 'poa') || null}
                    firstName={o.firstName}
                    answers={answers}
                    answerMeta={data.answerMeta || {}}
                    events={data.events || []}
                    canAct={isMine && !locked}
                    saving={setAnswer.isLoading}
                    onAnswer={answer}
                  />
                }
                // Claims Manager preferences (Nic 2026-11-02): who they would
                // like, woman or man or no preference, anything sensitive.
                // Every write is a checklist answer; shown on CM Assignment.
                cmPrefsPanel={
                  <CmPreferencesStep
                    step={(data.steps || []).find((s) => s.id === 'cm_preferences') || null}
                    firstName={o.firstName}
                    answers={answers}
                    answerMeta={data.answerMeta || {}}
                    claimsManagers={data.claimsManagers || []}
                    canAct={isMine && !locked}
                    saving={setAnswer.isLoading}
                    onAnswer={answer}
                  />
                }
                // Confirm service agreement (Nic 2026-10-23): the VA check,
                // with Correct & resend opening the agreement right here.
                saCheckPanel={
                  <SaCheckPanel
                    check={data.saCheck}
                    firstName={o.firstName}
                    need={(data.steps || []).find((s) => s.id === 'sa_confirmed')?.need}
                    canAct={canAct}
                    accepting={acceptSa.isLoading}
                    starting={startSa.isLoading}
                    onAccept={(note) =>
                      run(() => acceptSa.mutateAsync({ onboardingId, note }), 'Service agreement confirmed.')
                    }
                    onStart={() =>
                      run(() => startSa.mutateAsync({ onboardingId }), 'The robot is calling the VA now.')
                    }
                    onCorrect={() => {
                      setManageIntent((m) => ({ card: 'agreement', ask: 'correct', n: (m?.n || 0) + 1 }));
                      setTab('manage');
                    }}
                  />
                }
                data={data}
                answers={answers}
                locked={locked}
                active={activeStep}
                onPick={setActiveStep}
                onAnswer={answer}
                files={fileList}
                savingAddress={setAddress.isLoading}
                onSaveAddress={(address) =>
                  run(() => setAddress.mutateAsync({ onboardingId, address }), 'Address saved and verified.')
                }
              />
            ) : tab === 'files' ? (
              <FilesTab
                files={fileList}
                isLoading={files.isLoading}
                clientUserId={o.userId}
                stepTitles={stepTitles}
                onRenamed={() => queryClient.invalidateQueries(['claim-files', claimId])}
                // Past onboarding (with their CM, or held for one with the
                // claim In Process) the claim guard refuses it (QA 2026-10-13).
                canRename={isMine && !inNurture && !isDead && !withCm && !(readyForCm && o.carriedOver)}
                // Labeling follows the same rule as renaming.
                itemOptions={
                  isMine && !inNurture && !isDead && !withCm && !(readyForCm && o.carriedOver)
                    ? (data.steps || [])
                        .filter((st) => !st.auto && !st.staffOnly)
                        .map((st) => ({ value: st.id, label: st.title }))
                    : null
                }
                onLabel={async (file, item) => {
                  try {
                    await axios.put(`/client-onboarding/${onboardingId}/files/${file.fileId}/item`, { item });
                    toast.success(item === 'none' ? 'Marked as not a checklist item.' : 'Filed under that step.');
                    queryClient.invalidateQueries(['claim-files', claimId]);
                    queryClient.invalidateQueries(['client-onboarding', 'client', onboardingId]);
                  } catch (e) {
                    toast.error(e?.response?.data?.msg || "That file couldn't be labeled.");
                  }
                }}
                upload={
                  claimId && !locked ? (
                    <UploadForThem
                      claimId={claimId}
                      steps={data.steps || []}
                      onUploaded={() => {
                        queryClient.invalidateQueries(['claim-files', claimId]);
                        queryClient.invalidateQueries(['client-onboarding', 'client', onboardingId]);
                      }}
                    />
                  ) : null
                }
              />
            ) : tab === 'transcripts' ? (
              <div className={RAIL_PAD}>
                <LeadTranscriptsCard leadId={onboardingId} endpointBase="/client-onboarding" bare />
              </div>
            ) : tab === 'notes' ? (
              <NotesTab o={o} lead={data.lead} onboardingId={onboardingId} canEdit={isMine && !isDead} />
            ) : (
              <ManageTab o={o} onboardingId={onboardingId} canHandOff={canHandOff} canKill={canKill} greyed={greyed} onClose={onClose} intent={manageIntent} onIntentDone={() => setManageIntent((m) => (m ? { ...m, ask: null } : m))} />
            )}
          </TabPage>
        </section>
      </div>

      {status ? (
      <footer className="border-t border-slate-200/80 bg-white px-[20px] py-[10px] dark:border-white/10 dark:!bg-surface-elevated md:rounded-b-3xl">
        {status?.notYours ? (
          <NotYoursNote ownerName={o.ownerName} noun="list" viewOnly={viewOnly} />
        ) : status ? (
          <div className="flex flex-col gap-[10px] sm:flex-row sm:items-center">
            <div className="flex min-w-0 items-center gap-[12px] sm:mr-auto">
              <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', STATUS_TONE[status.tone])}>
                <status.icon size={17} strokeWidth={2.5} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{status.text}</div>
                {status.sub ? <div className="text-xs text-slate-500 dark:text-slate-400">{status.sub}</div> : null}
              </div>
            </div>
            {status.action ? (
              status.action.href ? (
                // A router link: no full page reload (QA 2026-10-13).
                <Link
                  to={status.action.href}
                  className="inline-flex h-9 items-center justify-center gap-[6px] whitespace-nowrap rounded-lg bg-emerald-600 px-[16px] text-sm font-semibold text-white no-underline hover:bg-emerald-500 hover:text-white"
                >
                  {status.action.label}
                </Link>
              ) : (
                <button
                  type="button"
                  disabled={status.action.busy || status.action.disabled}
                  title={status.action.title}
                  onClick={status.action.onClick}
                  className="inline-flex h-9 items-center justify-center gap-[6px] whitespace-nowrap rounded-lg bg-slate-900 px-[16px] text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-50 dark:!bg-slate-100 dark:!text-slate-900"
                >
                  {status.action.busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : status.action.icon ? (
                    <status.action.icon size={14} />
                  ) : null}
                  {status.action.label}
                </button>
              )
            ) : null}
          </div>
        ) : null}
        {/* Bring a stalled client back by hand: the reason lands on their
            history (Nic 2026-10-12). */}
        {inNurture && reengaging ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const reason = reengageReason.trim();
              if (!reason || !reengageReady(viewer, reengageTo)) return;
              try {
                const res = await reengage.mutateAsync({
                  onboardingId,
                  reason,
                  ...(viewer?.isManager ? { toUserId: reengageTo } : {}),
                });
                toast.success(`${o.firstName || 'They'} ${res?.ownerName ? `went to ${res.ownerName}` : 'are back on the board'}.`);
                setReengaging(false);
                setReengageReason('');
              } catch (err) {
                toast.error(err?.response?.data?.msg || "They couldn't be brought back.");
              }
            }}
            className="mt-[10px] flex flex-wrap items-center gap-[8px]"
          >
            <label htmlFor="reengage-reason" className="sr-only">
              Why they&apos;re coming back
            </label>
            <input
              id="reengage-reason"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={reengageReason}
              maxLength={200}
              onChange={(e) => setReengageReason(e.target.value)}
              placeholder="Why? e.g. called from his wife's phone"
              className="h-9 min-w-[14rem] flex-1 rounded-lg bg-white px-[12px] text-sm text-slate-700 outline-none ring-1 ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-sky-400/60 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
            />
            <ReengagePicker
              viewer={viewer}
              noun="specialist"
              value={reengageTo}
              onChange={setReengageTo}
              options={(reengageSpecialists.data?.specialists || []).map((sp) => ({
                value: sp.userId,
                label: sp.name,
              }))}
            />
            <button
              type="submit"
              disabled={!reengageReason.trim() || !reengageReady(viewer, reengageTo) || reengage.isLoading}
              className="inline-flex h-9 items-center gap-[6px] whitespace-nowrap rounded-lg bg-slate-900 px-[16px] text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
            >
              {reengage.isLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Bring back
            </button>
            <button
              type="button"
              onClick={() => {
                setReengaging(false);
                setReengageReason('');
              }}
              className="h-9 whitespace-nowrap rounded-lg px-[12px] text-sm font-semibold text-slate-500 outline-none hover:bg-slate-100 focus:outline-none dark:text-slate-400 dark:hover:!bg-white/10"
            >
              Cancel
            </button>
          </form>
        ) : null}
      </footer>
      ) : null}

      {booking ? (
        <SlotPickerOverlay
          mount="onboarding"
          leadId={onboardingId}
          leadName={o.name}
          onClose={() => setBooking(false)}
          onBooked={() => toast.success('Booked. It is on your Meetings calendar.')}
        />
      ) : null}
      {moving ? (
        <SlotPickerOverlay
          mount="onboarding"
          leadId={onboardingId}
          leadName={o.name}
          reschedule={{ bookingId: moving.calendlyBookingId, kind: moving.kind, timezone: moving.timezone }}
          onClose={() => setMoving(null)}
          onBooked={() => toast.success('Moved. They get the new time by text and email.')}
        />
      ) : null}
      <NudgeComposer
        open={!!composer}
        blank={composer === 'text'}
        draftKey={onboardingId}
        name={o.name}
        loadDraft={() => fetchOnboardingTextDraft(onboardingId)}
        sendText={({ text, rcAccessToken }) => sendText.mutateAsync({ onboardingId, text, rcAccessToken })}
        sending={sendText.isLoading}
        onClose={() => setComposer(false)}
      />
    </WorkspaceFrame>,
    document.body,
  );
};

export default ClientWorkspace;
